import type { KpiFormat } from "../lib/format";
import { formatValue } from "../lib/format";
import { GATE_COPY } from "../lib/sanityGate";
import { domainVar } from "../lib/widgetDomain.mjs";
import WidgetNote from "./WidgetNote";
import type { NoteSection } from "../lib/widgetNote.mjs";

// MER-317: copy shown when a single-number widget's query succeeded but
// produced zero rows in the selected range. AJ's show-data-not-hide-gaps
// rule: replace the bare "n/a" string with explanatory text so the
// reader sees the tile is wired and the range is just sparse.
// Wording owned by AJ; do not edit without ticket sign-off.
export const EMPTY_RANGE_COPY = "Limited activity this period. Try a wider range.";

export interface KPITileProps {
  title: string;
  // MER-306 close: widened to string so KPI tiles can surface text
  // categoricals (route_kpis.route_margin_trend_direction). Numeric tiles
  // still pass numbers through unchanged.
  value: number | string | null;
  format: KpiFormat;
  flagLevel?: "GREEN" | "YELLOW" | "RED";
  delta?: number | null;
  caption?: string;
  // MER-317: when true, render the limited-activity copy in place of the
  // formatted value. The KPI tile still keeps its title + caption so the
  // widget identity stays on screen; only the value rendering changes.
  isEmpty?: boolean;
  // MER-636: override EMPTY_RANGE_COPY for the one empty case it describes
  // wrongly -- a selected range that cannot address a single period bucket at
  // this tile's grain (week to date on monthly rows). The tile is empty
  // because of the arithmetic, not because the period was quiet, so telling
  // the reader to "try a wider range" states the wrong cause and leaves out
  // the Period selector. Unset everywhere else, which keeps EMPTY_RANGE_COPY
  // on the sparse-range case it was written for.
  emptyCopy?: string;
  // MER-441: render the "Pending: insufficient source data" state (same muted
  // slot as isEmpty) when the value is gated as impossible / starved.
  isGated?: boolean;
  // MER-449: override the gated copy. Defaults to GATE_COPY ("awaiting source
  // data") for feed gaps; the resolver passes REVIEW_COPY for calc/binding
  // bugs we are actively fixing.
  gateCopy?: string;
  // MER-584: render the "Open source view" footer. WidgetRenderer passes false
  // for gated and empty tiles -- MER-571's rule is that a tile withholding its
  // number must not look like a door.
  drillable?: boolean;
  // MER-625: what the footer says when this tile drills into another tab
  // rather than opening the row-level modal ("Break down in Operations").
  // Unset means the default "Open source view", which is what a row dump is.
  // The reader is told which of the two a click does before they spend it.
  drillLabel?: string;
  // MER-580: period / scope line. Rendered under the title instead of
  // concatenated into it, so a long range label wraps on its own line rather
  // than pushing the number down a narrow card.
  periodNote?: string;
  // MER-578: which part of the business this tile measures. Drives the 3px
  // top rail. Resolved by widgetDomain(); defaults to operations so an
  // unmapped tab still gets a stable rail rather than an unstyled edge.
  domain?: string;
  // MER-604 / MER-558: the tile-info panel's sections, already through
  // noteSections(). Rendered behind an info control in the tile chrome, never
  // on the face of the card -- the caption slot belongs to what_it_shows.
  // Passed on gated and empty tiles too: a definition is not a drill
  // affordance, so MER-571's rule is untouched, and a tile withholding its
  // number is exactly where a reader most wants to know what the number would
  // have been and what to do about it.
  sections?: NoteSection[];
}

/**
 * Single Number KPI widget. Maps to manifest chart_type=single_number.
 * FLAG entity drives flagLevel; renderer reads the most recent FLAG row
 * for this KPI x entity x period.
 */
// MER-170: small icons next to FLAG colors so color-blind users (~8% of
// men can't tell GREEN from RED reliably) can still read the level.
// `aria-hidden` keeps these out of the SR announcement; the FLAG state
// is conveyed in the article's aria-label instead.
const FLAG_ICON: Record<"GREEN" | "YELLOW" | "RED", string> = {
  GREEN: "✓", // checkmark
  YELLOW: "⚠", // warning sign
  RED: "!",
};

// MER-578: the flag reads as a labelled chip, not a coloured glyph. Identity
// is never carried by colour alone -- roughly 8% of men cannot separate the
// green from the red, and a bare tinted number tells them nothing. The wording
// is the operator's, from AJ's preview.
const FLAG_LABEL: Record<"GREEN" | "YELLOW" | "RED", string> = {
  GREEN: "in band",
  YELLOW: "watch",
  RED: "out of band",
};

const FLAG_WASH: Record<"GREEN" | "YELLOW" | "RED", string> = {
  GREEN: "var(--flag-green-wash)",
  YELLOW: "var(--flag-yellow-wash)",
  RED: "var(--flag-red-wash)",
};

export default function KPITile(props: KPITileProps) {
  const { title, value, format, flagLevel, delta, caption, isEmpty, isGated, gateCopy } = props;
  const domain = props.domain ?? "operations";
  const periodNote = props.periodNote;
  const sections = props.sections && props.sections.length > 0 ? props.sections : null;
  const drillable = Boolean(props.drillable) && !isEmpty && !isGated;
  const flagColor =
    flagLevel === "RED"
      ? "var(--flag-red)"
      : flagLevel === "YELLOW"
        ? "var(--flag-yellow)"
        : flagLevel === "GREEN"
          ? "var(--flag-green)"
          : "var(--text-primary)";

  // MER-317: empty-range tiles render the limited-activity copy in place
  // of the formatted value. Title + caption stay visible so the widget
  // identity remains on screen; only the value glyph changes. A FLAG
  // icon would be misleading on an empty tile (nothing measured), so we
  // suppress it in the empty state and downsize the empty copy.
  // MER-441: a gated value (impossible figure from a starved denominator)
  // renders the same muted slot as an empty range, with honest "insufficient
  // source data" copy instead of the number.
  const muted = Boolean(isEmpty || isGated);
  // MER-449: a gated tile uses its resolver-supplied copy (REVIEW_COPY for a
  // calc bug, GATE_COPY for a feed gap); fall back to GATE_COPY when unset.
  const mutedCopy = isGated ? (gateCopy ?? GATE_COPY) : (props.emptyCopy ?? EMPTY_RANGE_COPY);
  const formattedValue = muted ? mutedCopy : formatValue(value, format);
  const ariaLabel = muted
    ? `${title}: ${mutedCopy}`
    : flagLevel
      ? `${title}: ${formattedValue} (${FLAG_LABEL[flagLevel]})`
      : `${title}: ${formattedValue}`;

  return (
    <article
      role="article"
      aria-label={ariaLabel}
      style={{
        background: "var(--panel)",
        border: "0.5px solid var(--border)",
        // MER-578: the domain rail. Three pixels of colour at the top of every
        // tile, keyed to the part of the business it measures, is what turns a
        // page of white cards into something a reader can navigate by eye.
        borderTop: `3px solid ${domainVar(domain)}`,
        borderRadius: 6,
        padding: "14px 16px",
        // MER-580: the GRID owns the width. A 180px minimum here painted the
        // tile wider than its track whenever a row squeezed a card to one
        // column, so tiles overlapped their neighbours and captions clipped
        // mid-word. planSectionRows now guarantees a 2-track minimum; this
        // must not fight it.
        minWidth: 0,
        boxShadow: "var(--shadow-tile)",
        // MER-189: tile fills its grid cell so siblings in the same
        // row align top + bottom. Grid default align-items stretches
        // the wrapper; height:100% pulls the article to fill it.
        // Flex column lets caption marginTop:auto pin the description
        // to the bottom of the card.
        height: "100%",
        display: "flex",
        flexDirection: "column",
        // MER-604: anchors the widget-note control to the card's top-right
        // corner. No effect on a tile without a note.
        position: "relative",
      }}
    >
      <header
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: "var(--text-2)",
          fontWeight: 600,
          marginBottom: 6,
          // MER-604: reserve the corner the note control sits in, so a long
          // title wraps clear of it instead of running underneath.
          paddingRight: sections ? 20 : 0,
        }}
      >
        {title}
      </header>
      {periodNote ? (
        <div
          style={{
            // MER-584: a chip on the domain wash, per the mockup's MON badge.
            // As a plain muted line it read as part of the caption; as a chip
            // it reads as the scope the number belongs to.
            alignSelf: "flex-start",
            marginTop: -2,
            marginBottom: 6,
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: domainVar(domain),
            background: `var(--domain-${domain}-wash)`,
            padding: "1px 6px",
            borderRadius: 3,
            lineHeight: 1.5,
          }}
        >
          {periodNote}
        </div>
      ) : null}
      <div
        style={{
          // MER-317: shrink + mute the value slot when we're showing the
          // limited-activity copy. Keeps the tile from screaming a 19px
          // bold sentence at the reader while still keeping the message
          // legible. The numeric path keeps the original 19px/700.
          // MER-578: tabular-nums in a mono face so digits line up down a
          // column and a reader can compare tiles vertically without
          // re-reading each one. The muted copy is prose, so it keeps the
          // proportional face.
          fontFamily: muted ? "inherit" : "var(--font-mono)",
          fontVariantNumeric: muted ? "normal" : "tabular-nums",
          fontSize: muted ? 12 : 20,
          fontWeight: muted ? 500 : 600,
          // MER-578: the VALUE is not flag-coloured. State is carried by the
          // chip below, which pairs the colour with an icon and a word. Tinting
          // the number as well double-encodes the same fact and makes a RED
          // figure harder to read than the healthy one beside it. Text wears
          // text tokens; the chip beside it carries identity.
          color: muted ? "var(--text-muted)" : "var(--text-primary)",
          letterSpacing: muted ? 0 : -0.4,
          lineHeight: muted ? 1.35 : 1.1,
        }}
      >
        {formattedValue}
      </div>
      {flagLevel && !muted ? (
        <div
          style={{
            marginTop: 5,
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 9.5,
            fontWeight: 600,
            letterSpacing: 0.02,
            padding: "1px 6px",
            borderRadius: 3,
            color: flagColor,
            background: FLAG_WASH[flagLevel],
          }}
        >
          <span aria-hidden="true">{FLAG_ICON[flagLevel]}</span>
          {FLAG_LABEL[flagLevel]}
        </div>
      ) : null}
      {delta !== undefined && delta !== null && !muted ? (
        <div
          style={{
            // MER-584: the delta carries its direction. Rendering a rise and a
            // fall in the same muted grey made the reader parse the sign to
            // learn which way a number moved -- the one thing a trend line
            // exists to say at a glance.
            fontSize: 10,
            fontFamily: "var(--font-mono)",
            color:
              delta > 0 ? "var(--flag-green)" : delta < 0 ? "var(--flag-red)" : "var(--text-2)",
            marginTop: 4,
            fontWeight: 600,
          }}
        >
          {delta > 0 ? "+" : ""}
          {delta.toFixed(1)}% vs prior
        </div>
      ) : null}
      {/* MER-189: caption slot reserves vertical space even when the
          manifest omits what_it_shows so cards with and without
          descriptions match heights in the same row. marginTop:auto
          pushes the caption to the bottom of the flex column. */}
      <div
        style={{
          fontSize: 10,
          color: "var(--text-muted)",
          marginTop: "auto",
          paddingTop: 4,
          minHeight: 28,
          lineHeight: 1.35,
        }}
      >
        {caption ?? ""}
      </div>
      {/* MER-604: the technical definition, on demand. Control anchors to the
          card's top-right; the panel opens here, below the caption and above
          the drill footer, so the footer stays the card's last line. */}
      {sections ? <WidgetNote sections={sections} label={title} domain={domain} /> : null}
      {drillable ? (
        <div
          style={{
            // MER-584: the mockup's "Open source view" row. The whole tile is
            // already the click target; this makes that legible instead of
            // leaving the reader to discover it by hovering.
            marginTop: 10,
            paddingTop: 8,
            borderTop: "0.5px solid var(--border)",
            fontSize: 10.5,
            fontWeight: 600,
            color: domainVar(domain),
          }}
        >
          {props.drillLabel ?? "Open source view"} ↗
        </div>
      ) : null}
    </article>
  );
}
